import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOnboardingStatus, useUpdateOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { logger } from '@/lib/logger';

interface TourStep {
  target: string;
  title: string;
  description: string;
  route: string;
}

const tourSteps: TourStep[] = [
  {
    target: 'dashboard',
    title: 'Dashboard',
    description: 'Il tuo centro di controllo. Qui vedi statistiche, concerti in arrivo e azioni rapide.',
    route: '/dashboard',
  },
  {
    target: 'concerts',
    title: 'Concerti',
    description: 'Gestisci tutti i tuoi concerti: crea, modifica, duplica e monitora lo stato di ciascun evento.',
    route: '/concerts',
  },
  {
    target: 'calendar',
    title: 'Calendario',
    description: 'Visualizza i tuoi concerti nel calendario mensile. Clicca su un evento per i dettagli.',
    route: '/calendar',
  },
  {
    target: 'contacts',
    title: 'Contatti',
    description: 'Il tuo database di artisti, promoter, venue manager e fornitori di servizi.',
    route: '/contacts',
  },
  {
    target: 'revenue-shares',
    title: 'Revenue Shares',
    description: 'Calcola e traccia le divisioni di incasso per ogni concerto.',
    route: '/revenue-shares',
  },
];

export const TourGuide = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: status, isLoading } = useOnboardingStatus();
  const updateStatus = useUpdateOnboardingStatus();

  // Migrate existing localStorage data to database (one-time)
  useEffect(() => {
    if (!status || isLoading) return;
    
    const localTourCompleted = localStorage.getItem('tourCompleted');
    if (localTourCompleted === 'true' && !status.tourCompleted) {
      // Migrate localStorage flag to database
      updateStatus.mutate({ tourCompleted: true });
    }
  }, [status, isLoading]);

  // Show tour if not completed in database
  useEffect(() => {
    if (isLoading || !status) return;
    
    if (!status.tourCompleted && location.pathname === '/dashboard') {
      setTimeout(() => setIsActive(true), 1000);
    }
  }, [location, status, isLoading]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      navigate(tourSteps[nextStep].route);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      navigate(tourSteps[prevStep].route);
    }
  };

  const completeTour = async () => {
    try {
      await updateStatus.mutateAsync({ tourCompleted: true });
      setIsActive(false);
    } catch (error) {
      logger.error('Failed to complete tour:', error);
      setIsActive(false);
    }
  };

  const skipTour = async () => {
    try {
      await updateStatus.mutateAsync({ tourCompleted: true });
      setIsActive(false);
    } catch (error) {
      logger.error('Failed to skip tour:', error);
      setIsActive(false);
    }
  };

  // Don't show if tour is completed or still loading
  if (!isActive || status?.tourCompleted || isLoading) return null;

  const step = tourSteps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <Card className={cn(
        "w-full max-w-md shadow-2xl animate-in fade-in slide-in-from-bottom-8 duration-300",
        "sm:slide-in-from-bottom-0"
      )}>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1 flex-1">
              <h3 className="text-heading-5">{step.title}</h3>
              <p className="text-body-small">{step.description}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={skipTour}
              className="flex-shrink-0 h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  index === currentStep ? "bg-primary flex-1" : "bg-muted w-8"
                )}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <Button
              variant="outline"
              onClick={skipTour}
              size="sm"
            >
              Salta tour
            </Button>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  size="sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Indietro
                </Button>
              )}
              <Button
                onClick={handleNext}
                size="sm"
              >
                {currentStep < tourSteps.length - 1 ? (
                  <>
                    Avanti
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  'Completa'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
